import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { Check, Copy, Mail, QrCode } from "lucide-react";
import { Button } from "@/fileshare/components/ui/button";
import { copyToClipboard } from "@/fileshare/lib/clipboard";

/**
 * Share actions for a download link: copy, email (mailto) and a QR code
 * for instant mobile access. Used on the upload result and download pages.
 */
export default function ShareActions({ url, title = "your files" }) {
  const [copied, setCopied] = useState(false);
  const [showQr, setShowQr] = useState(true);

  const copy = async () => {
    const ok = await copyToClipboard(url);
    if (ok) {
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 1800);
    } else {
      toast.error("Could not copy — copy the link manually");
    }
  };

  const mailto = `mailto:?subject=${encodeURIComponent(`Files shared with you — ${title}`)}&body=${encodeURIComponent(`Hi,\n\nI've shared ${title} with you via FileShare. Download here:\n\n${url}\n\nThe link may expire, so grab it soon!`)}`;

  return (
    <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50/50 p-4" data-testid="share-actions">
      <div className="flex flex-wrap items-center gap-4">
        {showQr && (
          <div className="bg-white p-2 rounded-md border border-slate-200 shrink-0" data-testid="share-qr">
            <QRCodeSVG value={url} size={96} fgColor="#0f172a" />
          </div>
        )}
        <div className="flex-1 min-w-[180px]">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1.5">
            <QrCode className="h-3.5 w-3.5" /> Scan to open on mobile
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              data-testid="share-copy-button"
              variant="outline"
              size="sm"
              onClick={copy}
              className="border-slate-200 text-slate-700 hover:bg-white gap-1.5"
            >
              {copied ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
              {copied ? "Copied" : "Copy link"}
            </Button>
            <a href={mailto} data-testid="share-email-button">
              <Button variant="outline" size="sm" className="border-slate-200 text-slate-700 hover:bg-white gap-1.5">
                <Mail className="h-3.5 w-3.5" /> Email link
              </Button>
            </a>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowQr((s) => !s)}
              className="text-slate-400 hover:text-slate-600"
            >
              {showQr ? "Hide QR" : "Show QR"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
